import {gsap} from "gsap/all";
import {PillGlow} from "./PillGlow.js";

const SVG_NS = "http://www.w3.org/2000/svg";
let uid = 0;

// Draws a button's pill shape on the fly: two rounded ends (caps) joined by
// straight lines, sized to the measured button so the ends stay perfectly round
// at any width. This is how the shape is built in Figma too.
export class PillButton {

  // Left cap radius as a fraction of height, right cap is H/2.
  static LEFT_RADIUS_RATIO = 52 / 140;

  // Default glow colors (the chromo gradient): teal -> pink -> orange.
  static DEFAULT_GLOW_COLORS = ["#49BACD", "#F36688", "#F9804F"];

  constructor(root) {
    this.root = root;
    root.pillButton = this; // so callers can reach the instance from the element
    this.inner = root.querySelector(".inner");
    if (!this.inner) return;

    this.rounded = root.classList.contains("rounded"); // equal caps, symmetric pill
    this.id = uid++;
    this.layers = [
      this.buildLayer(root.querySelector(".pill"), false),
      this.buildLayer(root.querySelector(".glow"), true),
    ].filter(Boolean);

    // Redraw when the width changes: a language switch (label text), or a button
    // that started hidden becoming visible. Not for window resizing.
    this.observer = new ResizeObserver(() => this.draw());
    this.observer.observe(this.inner);
    this.draw();
    this.animateGlow();

    // Spin the reload icon when the button is pressed.
    if (root.dataset.icon === "reload") {
      this.iconEl = root.querySelector(".icon svg");
      root.addEventListener("pointerdown", () => this.spinIcon());
    }
  }

  // Quick one-shot spin of the icon (used by the reload/restart button).
  spinIcon() {
    if (!this.iconEl) return;
    gsap.fromTo(this.iconEl,
      {rotation: 0},
      {rotation: 360, duration: 0.5, ease: "power2.out", transformOrigin: "50% 50%"});
  }

  // Slowly rotate the glow gradient so the colors swirl behind the pill.
  animateGlow() {
    if (!this.glowGradient) return;
    const state = {angle: 0};
    this.glowTween = gsap.to(state, {
      angle: 360,
      duration: 8,
      ease: "none",
      repeat: -1,
      onUpdate: () => {
        this.glowGradient.setAttribute("gradientTransform", `rotate(${state.angle} 0.5 0.5)`);
      },
    });
  }

  // Glow colors from the data-glow-colors attribute, or the default chromo.
  glowColors() {
    const attr = this.root.dataset.glowColors;
    if (attr) return attr.split(",").map((c) => c.trim()).filter(Boolean);
    return PillButton.DEFAULT_GLOW_COLORS;
  }

  // (Re)build the gradient stops, spread evenly across the gradient (horizontal).
  applyGlowColors(grad) {
    grad.textContent = "";
    const colors = this.glowColors();
    colors.forEach((color, i) => {
      const stop = document.createElementNS(SVG_NS, "stop");
      stop.setAttribute("offset", colors.length > 1 ? `${i / (colors.length - 1)}` : "0");
      stop.setAttribute("stop-color", color);
      grad.appendChild(stop);
    });
  }

  // Repaint the glow after the colors changed. Pass an array (or a comma
  // separated string) to set new colors, or nothing to re-read the button's
  // data-glow-colors attribute.
  refreshGlow(colors = null) {
    if (colors) this.root.dataset.glowColors = Array.isArray(colors) ? colors.join(",") : colors;
    if (this.glowGradient) this.applyGlowColors(this.glowGradient);
  }

  // Inject an <svg><path/></svg> into a layer; returns {svg, path} or null.
  buildLayer(host, isGlow) {
    if (!host) return null;

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("preserveAspectRatio", "none");
    const path = document.createElementNS(SVG_NS, "path");

    if (isGlow) {
      const gradId = `pill-glow-${this.id}`;
      const defs = document.createElementNS(SVG_NS, "defs");
      const grad = document.createElementNS(SVG_NS, "linearGradient");
      grad.setAttribute("id", gradId);
      grad.setAttribute("x1", "0");
      grad.setAttribute("y1", "0");
      grad.setAttribute("x2", "1");
      grad.setAttribute("y2", "0");

      this.applyGlowColors(grad);

      grad.setAttribute("gradientTransform", "rotate(0 0.5 0.5)");
      this.glowGradient = grad;
      defs.appendChild(grad);
      svg.appendChild(defs);
      path.setAttribute("fill", `url(#${gradId})`); // the pill's fill comes from CSS
    }

    svg.appendChild(path);
    host.appendChild(svg);
    return {svg, path};
  }

  draw() {
    const w = Math.round(this.inner.offsetWidth);
    const h = Math.round(this.inner.offsetHeight);
    if (w <= 0 || h <= 0) return; // hidden / not laid out yet

    const d = PillButton.buildPath(w, h, this.rounded);
    for (const {svg, path} of this.layers) {
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      path.setAttribute("d", d);
    }
  }

  destroy() {
    this.observer?.disconnect();
    this.glowTween?.kill();
  }

  // Builds the pill from two rounded ends (caps) joined by straight lines. When
  // the two caps are different sizes the lines slope, making the shape.
  // "rounded" makes both caps the same size.
  static buildPath(W, H, rounded = false) {
    const rRight = H / 2;
    const rLeft = rounded ? rRight : H * PillButton.LEFT_RADIUS_RATIO;
    const cy = H / 2;
    const cxLeft = rLeft;
    const cxRight = W - rRight;
    const dx = cxRight - cxLeft;

    // Direction of the sloped edge; it tilts more when the two caps differ in size.
    let nx = dx > 0 ? -(rRight - rLeft) / dx : 0;
    if (!isFinite(nx) || Math.abs(nx) >= 1) nx = 0;
    const ny = Math.sqrt(Math.max(0, 1 - nx * nx));

    // Points where the straight edges meet each cap (upper = smaller y).
    const luX = cxLeft + rLeft * nx;
    const luY = cy - rLeft * ny;
    const llX = cxLeft + rLeft * nx;
    const llY = cy + rLeft * ny;
    const ruX = cxRight + rRight * nx;
    const ruY = cy - rRight * ny;
    const rlX = cxRight + rRight * nx;
    const rlY = cy + rRight * ny;

    const n = (v) => Math.round(v * 100) / 100;

    return [
      `M ${n(luX)} ${n(luY)}`,
      `L ${n(ruX)} ${n(ruY)}`,
      `A ${n(rRight)} ${n(rRight)} 0 1 1 ${n(rlX)} ${n(rlY)}`, // right cap
      `L ${n(llX)} ${n(llY)}`,
      `A ${n(rLeft)} ${n(rLeft)} 0 0 1 ${n(luX)} ${n(luY)}`, // left cap
      "Z",
    ].join(" ");
  }

  // Repaint the glow of a button (element, selector or NodeList) after its
  // colors changed. Defaults to every pill-button on the page.
  static refreshGlow(target = ".pill-button", colors = null) {
    const els = typeof target === "string" ? document.querySelectorAll(target) : (target.length !== undefined ? target : [target]);
    for (const el of els) el.pillButton?.refreshGlow(colors);
  }

  // Build every .pill-button
  static initAll() {
    const buttons = Array.from(document.querySelectorAll(".pill-button")).map((el) => new PillButton(el));
    const crewGlows = Array.from(document.querySelectorAll(".crew-pill-button")).map((el) => new PillGlow(el));
    return [buttons, crewGlows];
  }
}
